import { Coins, Phone, CheckCircle2, ShieldCheck, ArrowRight, Building2, Star, HelpCircle } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function ErhvervsleasingDe() {
  const leasingEksempler = [
    { 
      mdr: '24 Monate', 
      prMnd: '187,- €', 
      udbetaling: '425,- €', 
      restvaerdi: '425,- €', 
      beskrivelse: 'Schnelle Abschreibung und kurze Bindung',
      popular: false
    },
    { 
      mdr: '36 Monate', 
      prMnd: '129,- €', 
      udbetaling: '425,- €', 
      restvaerdi: '425,- €', 
      beskrivelse: 'Die beliebteste Laufzeit für Reit- und Stallbetriebe',
      popular: true
    },
    { 
      mdr: '48 Monate', 
      prMnd: '97,- €', 
      udbetaling: '425,- €', 
      restvaerdi: '425,- €', 
      beskrivelse: 'Niedrigste Monatsrate, maximale Liquidität',
      popular: false
    }
  ];

  const trin = [
    { step: '01', title: 'Formular ausfüllen', desc: 'Übermitteln Sie uns Ihre Unternehmensdaten und die gewünschte Leasinglaufzeit.' },
    { step: '02', title: 'Schnelle Bonitätsprüfung', desc: 'Wir reichen den Vorgang bei unserem Leasingpartner Ikano Bank ein, der in der Regel innerhalb weniger Tage entscheidet.' },
    { step: '03', title: 'Lieferung & Start', desc: 'Nach digitaler Vertragsunterzeichnung fertigen wir Ihre Maschine an und liefern pünktlich an Ihren Stall.' }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Gewerbeleasing für Danshaker - Attraktives B2B-Leasing"
        description="Gewerbliches B2B-Leasing für Betriebe mit USt-IdNr./CVR ab nur 97 €/Monat. Schonen Sie Ihre Liquidität in Zusammenarbeit mit Ikano Bank Leasing."
        path="/de/gewerbeleasing"
        lang="de"
      />

      {/* B2B Dark Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 py-20 text-white border-b border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,103,162,0.15),transparent_50%)]" />
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/20 border border-[#0E67A2]/40 px-4 py-1.5 text-xs font-black text-sky-400 uppercase tracking-wider">
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Gewerbeleasing für Unternehmen</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight sm:text-6xl text-white">
              Gewerbeleasing
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 font-medium leading-relaxed max-w-2xl">
              Für Betriebe mit gültiger Firmennummer bieten wir attraktives B2B-Leasing in Zusammenarbeit mit <strong className="text-[#0E67A2]">Ikano Bank Leasing</strong>. Schonen Sie Ihr Eigenkapital und setzen Sie die Leasingraten steuerlich als Betriebsausgabe ab.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="#ansoeg-formular"
                className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3.5 text-sm font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-lg"
              >
                <span>Leasingangebot anfordern</span>
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="tel:25303589"
                className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-6 py-3.5 text-sm font-bold text-slate-300 hover:text-white transition hover:bg-slate-800"
              >
                <Phone className="h-4 w-4 text-[#0E67A2]" />
                <span>+45 2530 3589</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
        
        {/* Transparent Table / Pricing Cards */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-slate-950">Beispielrechnungen Gewerbeleasing</h2>
            <p className="mt-2 text-slate-700 font-medium">Transparente Konditionen für professionelle Stallbetriebe.</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {leasingEksempler.map((eks) => (
              <div 
                key={eks.mdr} 
                className={`relative rounded-3xl border-2 p-8 transition-all flex flex-col justify-between ${
                  eks.popular 
                    ? 'border-[#0E67A2] bg-white shadow-2xl scale-105 z-10' 
                    : 'border-slate-300 bg-white/70 shadow-sm hover:border-slate-400'
                }`}
              >
                {eks.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#0E67A2] px-4 py-1 text-xs font-black text-white uppercase tracking-wider">
                    Beliebteste Wahl
                  </div>
                )}
                
                <div>
                  <div className="flex justify-between items-baseline mb-4">
                    <h3 className="text-xl font-black text-slate-900">{eks.mdr}</h3>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Laufzeit</span>
                  </div>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-black text-slate-950">{eks.prMnd}</span>
                    <span className="text-xs text-slate-500 font-semibold block mt-1">/ Monat exkl. MwSt.</span>
                  </div>

                  <p className="text-xs text-slate-700 font-semibold mb-6 pb-6 border-b border-slate-200">
                    {eks.beskrivelse}
                  </p>

                  <div className="space-y-3 text-xs font-bold text-slate-700">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Anzahlung (10%):</span>
                      <span className="text-slate-900">{eks.udbetaling} exkl. MwSt.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Restwert (10%):</span>
                      <span className="text-slate-900">{eks.restvaerdi} exkl. MwSt.</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100">
                  <a
                    href="#ansoeg-formular"
                    className={`block w-full rounded-xl py-3 text-center text-xs font-bold transition shadow-xs ${
                      eks.popular
                        ? 'bg-[#0E67A2] text-white hover:bg-[#0E67A2]/90'
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                    }`}
                  >
                    Dieses Modell anfragen
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center text-xs text-slate-600 font-semibold">
            * Alle Angaben verstehen sich als Richtwerte zzgl. gesetzlicher MwSt. Vorbehaltlich positiver Bonitätsprüfung durch Ikano Bank Leasing.
          </div>
        </div>

        {/* 3 Steps Workflow */}
        <div className="mb-20 rounded-3xl bg-slate-900 p-8 md:p-12 text-white">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-black text-white">In 3 einfachen Schritten zum Leasing</h3>
            <p className="text-slate-400 text-sm mt-2 font-medium">Schnelle und unkomplizierte Bearbeitung direkt über unseren Partner Ikano Bank.</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {trin.map((t) => (
              <div key={t.step} className="border-l-2 border-[#0E67A2] pl-6 space-y-2">
                <span className="text-3xl font-black text-[#0E67A2]">{t.step}</span>
                <h4 className="text-lg font-bold text-white">{t.title}</h4>
                <p className="text-xs text-slate-300 font-medium leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Form Section with Document Upload */}
        <div id="ansoeg-formular" className="grid gap-12 lg:grid-cols-12 items-start">
          
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-xl space-y-6">
              <h3 className="text-2xl font-black text-slate-950">Vorteile von Gewerbeleasing</h3>
              
              <ul className="space-y-4 text-sm text-slate-800 font-medium">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span><strong>Volle Liquidität:</strong> Kein großer Einmalkauf, sondern planbare und niedrige Monatsraten.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span><strong>Steuerlich absetzbar:</strong> Leasingraten können direkt als laufende Betriebsausgabe geltend gemacht werden.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span><strong>Solider Partner:</strong> Zuverlässige Abwicklung über Ikano Bank Leasing.</span>
                </li>
              </ul>

              <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4 text-sky-900 text-xs leading-relaxed font-semibold">
                <strong>Unterlagencheck:</strong>
                Für eine schnelle Bewilligung laden Sie bitte direkt im Formular den aktuellen Jahresabschluss Ihres Betriebes hoch.
              </div>
            </div>

            <div className="rounded-3xl bg-slate-100 p-6 space-y-3">
              <div className="flex items-center gap-2 text-slate-950 font-bold">
                <HelpCircle className="h-5 w-5 text-[#0E67A2]" />
                <span>Brauchen Sie Unterstützung?</span>
              </div>
              <p className="text-xs text-slate-700 font-medium">
                Wir helfen Ihnen gerne bei Fragen zur Bonitätsprüfung und Antragstellung.
              </p>
              <a href="tel:25303589" className="inline-block text-xs font-bold text-[#0E67A2] hover:underline">
                Rufen Sie uns an: +45 2530 3589 →
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border-2 border-[#0E67A2] bg-white p-6 md:p-10 shadow-2xl">
              <div className="mb-8">
                <span className="text-xs font-black uppercase tracking-wider text-[#0E67A2]">Unverbindlicher Antrag</span>
                <h3 className="text-2xl md:text-3xl font-black text-slate-950 mt-1">Gewerbeleasing anfragen</h3>
                <p className="mt-2 text-sm text-slate-700 font-medium">
                  Tragen Sie Ihre Firmendaten ein und laden Sie Ihren Jahresabschluss hoch – wir leiten alles diskret in die Wege.
                </p>
              </div>

              <ContactForm 
                defaultService="Gewerbeleasing (Ikano Bank)" 
                buttonText="Leasingantrag einreichen" 
                isErhvervsleasing={true} 
                lang="de"
              />
            </div>
          </div>

        </div>

      </section>
    </div>
  );
}
